import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Button } from "@mui/material";
import { privilegeAPI, masterDataAPI, menuAPI, userAPI } from "../services/api";
import Swal from "sweetalert2";
import $ from "jquery";
import "select2";
import "select2/dist/css/select2.min.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "./UserManagement.css";
import "./AddEditUser.css";
import "./AddEditPrivilege.css";
import { getRoutePermissions } from "../utils/auth";

const AddEditPrivilege = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { privCode: privCodeParam } = useParams();
  const privCode = privCodeParam ? decodeURIComponent(privCodeParam) : "";
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const preloadMode = searchParams.get("mode") || "";
  const preloadUserIdCode = searchParams.get("userIdCode") || "";
  const preloadUserTypeCode = searchParams.get("userTypeCode") || "";
  const useAdminMenuLabels = /\/privileges\/(add|edit)\b/.test(location.pathname);
  const isEditMode = /\/privileges\/edit\//.test(location.pathname);
  const isUserMappingEdit = !isEditMode && preloadMode === "user" && Boolean(preloadUserIdCode);
  const isLockedTarget = isEditMode || isUserMappingEdit;
  const routePermissions = getRoutePermissions("/privileges");
  const { canAdd, canEdit } = routePermissions;
  const canSave = isLockedTarget ? canEdit : canAdd;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [assignmentMode, setAssignmentMode] = useState(
    isUserMappingEdit ? "user" : "userType"
  ); // "userType" or "user"
  const [selectedUserType, setSelectedUserType] = useState("");
  const [selectedUserTypeForUser, setSelectedUserTypeForUser] = useState(
    isUserMappingEdit ? preloadUserTypeCode : ""
  );
  const [selectedUser, setSelectedUser] = useState(isUserMappingEdit ? preloadUserIdCode : "");
  const [roleUserCount, setRoleUserCount] = useState(null);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [privileges, setPrivileges] = useState({}); // { menuCode: { view, add, edit, delete, download } }
  const editLoadedRef = useRef(false);

  const [dropdowns, setDropdowns] = useState({
    userTypes: [],
    users: [],
    organizedMenus: { rootMenus: [], submenus: [], submenusByParent: new Map() },
  });

  const userTypeSelectRef = useRef(null);
  const userTypeForUserSelectRef = useRef(null); // User Role dropdown in user mode
  const userSelectRef = useRef(null);
  const isMountedRef = useRef(true);

  // Filter users based on selected userTypeCode in user mode
  const filteredUsers = useMemo(() => {
    if (assignmentMode === "user" && selectedUserTypeForUser) {
      return dropdowns.users.filter(
        (user) =>
          (user.userTypeCode || user.UserTypeCode) === selectedUserTypeForUser
      );
    }
    return dropdowns.users;
  }, [dropdowns.users, selectedUserTypeForUser, assignmentMode]);

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  const buildReturnState = () => {
    if (location.state?.restorePrivilegesList) {
      return {
        restorePrivilegesList: true,
        assignmentMode: location.state.assignmentMode || "userType",
        filters: location.state.filters || {
          userTypeCode: "",
          userIdCode: "all",
          menuCode: "all",
        },
      };
    }

    if (isUserMappingEdit) {
      return {
        restorePrivilegesList: true,
        assignmentMode: "user",
        filters: {
          userTypeCode: preloadUserTypeCode || selectedUserTypeForUser || "",
          userIdCode: "all",
          menuCode: "all",
        },
      };
    }

    if (assignmentMode === "user") {
      return {
        restorePrivilegesList: true,
        assignmentMode: "user",
        filters: {
          userTypeCode: selectedUserTypeForUser || "",
          userIdCode: selectedUser || "all",
          menuCode: "all",
        },
      };
    }

    return {
      restorePrivilegesList: true,
      assignmentMode: "userType",
      filters: {
        userTypeCode: selectedUserType || "",
        userIdCode: "all",
        menuCode: "all",
      },
    };
  };

  const goBackToList = () => {
    navigate("/privileges", { state: buildReturnState() });
  };

  useEffect(() => {
    if (!canSave) {
      Swal.fire({
        title: "Access Denied",
        text: `You do not have permission to ${isLockedTarget ? "edit" : "add"} privileges.`,
        icon: "error",
        confirmButtonText: "OK",
      }).then(() => goBackToList());
      return;
    }
    isMountedRef.current = true;
    editLoadedRef.current = false;
    loadDropdowns();

    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useAdminMenuLabels, canSave, privCode, isEditMode, isUserMappingEdit]);

  // Initialize Select2 for User Role
  useEffect(() => {
    if (!isMountedRef.current || assignmentMode !== "userType") return;

    const cleanupSelect2 = () => {
      if (!userTypeSelectRef.current) return;
      
      try {
        const element = userTypeSelectRef.current;
        
        // Check if element is still in DOM
        let isInDOM = false;
        try {
          isInDOM = element.parentNode !== null || document.body.contains(element);
        } catch (e) {
          // Element might be detached, skip cleanup
          return;
        }

        if (!isInDOM) {
          return;
        }

        const $select = $(element);
        if ($select.data("select2")) {
          // Remove event handlers first
          try {
            $select.off("change select2:select");
          } catch (e) {
            // Ignore if handlers already removed
          }
          
          // Destroy Select2 instance
          try {
            // Double-check element is still in DOM before destroying
            if (element.parentNode || document.body.contains(element)) {
              $select.select2("destroy");
            }
          } catch (destroyError) {
            // Element might already be removed by React, ignore silently
            // Don't log warnings for expected cleanup errors
          }
        }
      } catch (e) {
        // Silently ignore cleanup errors during unmount
        // These are expected when React removes elements
      }
    };

    const timeoutId = setTimeout(() => {
      if (!isMountedRef.current || !userTypeSelectRef.current) return;

      // Check if element is still in DOM before initializing
      const element = userTypeSelectRef.current;
      let isInDOM = false;
      try {
        isInDOM = element.parentNode !== null || document.body.contains(element);
      } catch (e) {
        // Element might be detached, skip initialization
        return;
      }

      if (!isInDOM || !isMountedRef.current) {
        return;
      }

      cleanupSelect2();

      if (!isMountedRef.current || !userTypeSelectRef.current) return;

      if (userTypeSelectRef.current && dropdowns.userTypes.length > 0 && isMountedRef.current && assignmentMode === "userType") {
        try {
          // Double-check element is still valid
          const currentElement = userTypeSelectRef.current;
          if (!currentElement || (!currentElement.parentNode && !document.body.contains(currentElement))) {
            return;
          }

          const $userTypeSelect = $(currentElement);
          $userTypeSelect
            .off("change select2:select")
            .select2({
              width: "100%",
              placeholder: "Select User Role",
            })
            .on("change select2:select", function (e) {
              if (!isMountedRef.current) return;
              const value = e.params?.data?.id || $(this).val() || "";
              handleUserTypeChange(value);
            });
          
          if (selectedUserType && isMountedRef.current && userTypeSelectRef.current) {
            $userTypeSelect.val(selectedUserType).trigger("change");
          }
        } catch (initError) {
          console.warn("Error initializing Select2:", initError);
        }
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      // Immediate cleanup - React will handle DOM removal
      if (isMountedRef.current) {
        cleanupSelect2();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdowns.userTypes, assignmentMode]);

  // Initialize Select2 for User Role dropdown (in user mode)
  useEffect(() => {
    if (!isMountedRef.current || assignmentMode !== "user") return;

    const cleanupSelect2 = () => {
      if (!userTypeForUserSelectRef.current) return;
      
      try {
        const element = userTypeForUserSelectRef.current;
        let isInDOM = false;
        try {
          isInDOM = element.parentNode !== null || document.body.contains(element);
        } catch (e) {
          return;
        }

        if (!isInDOM) {
          return;
        }

        const $select = $(element);
        if ($select.data("select2")) {
          try {
            $select.off("change select2:select");
          } catch (e) {
            // Ignore
          }
          
          try {
            if (element.parentNode || document.body.contains(element)) {
              $select.select2("destroy");
            }
          } catch (destroyError) {
            // Ignore
          }
        }
      } catch (e) {
        // Ignore
      }
    };

    const timeoutId = setTimeout(() => {
      if (!isMountedRef.current || !userTypeForUserSelectRef.current || assignmentMode !== "user") return;

      const element = userTypeForUserSelectRef.current;
      let isInDOM = false;
      try {
        isInDOM = element.parentNode !== null || document.body.contains(element);
      } catch (e) {
        return;
      }

      if (!isInDOM || !isMountedRef.current) {
        return;
      }

      if (!isMountedRef.current || !userTypeForUserSelectRef.current) return;

      if (userTypeForUserSelectRef.current && dropdowns.userTypes.length > 0 && isMountedRef.current) {
        try {
          const currentElement = userTypeForUserSelectRef.current;
          if (!currentElement || (!currentElement.parentNode && !document.body.contains(currentElement))) {
            return;
          }

          const $userTypeSelect = $(currentElement);

          // Avoid destroy/recreate churn (causes page jump) when Select2 already exists
          if ($userTypeSelect.data("select2")) {
            if (selectedUserTypeForUser) {
              $userTypeSelect.val(selectedUserTypeForUser).trigger("change.select2");
            }
            $userTypeSelect.prop("disabled", isUserMappingEdit);
            return;
          }

          $userTypeSelect
            .off("change select2:select")
            .select2({
              width: "100%",
              placeholder: "Select User Role",
              disabled: isUserMappingEdit,
            })
            .on("change select2:select", function (e) {
              if (!isMountedRef.current || isUserMappingEdit) return;
              const value = e.params?.data?.id || $(this).val() || "";
              setSelectedUserTypeForUser(value);
              setSelectedUser("");
              setPrivileges({});
            });
          
          if (selectedUserTypeForUser && isMountedRef.current && userTypeForUserSelectRef.current) {
            $userTypeSelect.val(selectedUserTypeForUser).trigger("change.select2");
          }
        } catch (initError) {
          console.warn("Error initializing Select2 for user role in user mode:", initError);
        }
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      // Only destroy on unmount / mode leave — not on every value update
      if (!isMountedRef.current || assignmentMode !== "user") {
        cleanupSelect2();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdowns.userTypes, assignmentMode, selectedUserTypeForUser, isUserMappingEdit]);

  // Initialize Select2 for User dropdown (in user mode)
  useEffect(() => {
    if (!isMountedRef.current || assignmentMode !== "user") return;

    const cleanupSelect2 = () => {
      if (!userSelectRef.current) return;
      
      try {
        const element = userSelectRef.current;
        let isInDOM = false;
        try {
          isInDOM = element.parentNode !== null || document.body.contains(element);
        } catch (e) {
          return;
        }

        if (!isInDOM) {
          return;
        }

        const $select = $(element);
        if ($select.data("select2")) {
          try {
            $select.off("change select2:select");
          } catch (e) {
            // Ignore
          }
          
          try {
            if (element.parentNode || document.body.contains(element)) {
              $select.select2("destroy");
            }
          } catch (destroyError) {
            // Ignore
          }
        }
      } catch (e) {
        // Ignore
      }
    };

    const timeoutId = setTimeout(() => {
      if (!isMountedRef.current || !userSelectRef.current || assignmentMode !== "user") return;

      const element = userSelectRef.current;
      let isInDOM = false;
      try {
        isInDOM = element.parentNode !== null || document.body.contains(element);
      } catch (e) {
        return;
      }

      if (!isInDOM || !isMountedRef.current) {
        return;
      }

      if (!isMountedRef.current || !userSelectRef.current) return;

      if (userSelectRef.current && filteredUsers.length >= 0 && isMountedRef.current) {
        try {
          const currentElement = userSelectRef.current;
          if (!currentElement || (!currentElement.parentNode && !document.body.contains(currentElement))) {
            return;
          }

          const $userSelect = $(currentElement);
          const userMatch = filteredUsers.some(
            (u) => (u.userIdCode || u.UserIdCode) === selectedUser
          );

          if ($userSelect.data("select2")) {
            if (selectedUser && userMatch) {
              $userSelect.val(selectedUser).trigger("change.select2");
            }
            $userSelect.prop("disabled", isUserMappingEdit || !selectedUserTypeForUser);
            return;
          }
          
          $userSelect
            .off("change select2:select")
            .select2({
              width: "100%",
              placeholder: selectedUserTypeForUser ? "Select User" : "Select User Role First",
              disabled: isUserMappingEdit || !selectedUserTypeForUser,
            })
            .on("change select2:select", function (e) {
              if (!isMountedRef.current || isUserMappingEdit) return;
              const value = e.params?.data?.id || $(this).val() || "";
              handleUserChange(value);
            });
          
          if (selectedUser && userMatch) {
            $userSelect.val(selectedUser).trigger("change.select2");
            if (isUserMappingEdit) {
              loadEffectivePrivilegesForUser(selectedUser, selectedUserTypeForUser);
            }
          } else if (!isUserMappingEdit && !selectedUser) {
            $userSelect.val("").trigger("change.select2");
          }
        } catch (initError) {
          console.warn("Error initializing Select2 for user:", initError);
        }
      }
    }, 150);

    return () => {
      clearTimeout(timeoutId);
      if (!isMountedRef.current || assignmentMode !== "user") {
        cleanupSelect2();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredUsers, assignmentMode, selectedUserTypeForUser, selectedUser, isUserMappingEdit]);

  // Organize menus hierarchically
  const organizeMenus = (menus) => {
    if (!menus || !Array.isArray(menus)) return { rootMenus: [], submenus: [], submenusByParent: new Map() };

    const rootMenus = [];
    const submenus = [];
    const submenusByParent = new Map();

    menus.forEach((menu) => {
      const parentCode = menu.parentMenuCode || menu.ParentMenuCode || "0000";
      const isRoot = parentCode === "0000" || parentCode === null || parentCode === "";

      if (isRoot) {
        rootMenus.push(menu);
      } else {
        submenus.push(menu);
        if (!submenusByParent.has(parentCode)) {
          submenusByParent.set(parentCode, []);
        }
        submenusByParent.get(parentCode).push(menu);
      }
    });

    // Sort by display order
    rootMenus.sort((a, b) => {
      const orderA = a.displayOrder || a.DisplayOrder || 0;
      const orderB = b.displayOrder || b.DisplayOrder || 0;
      if (orderA !== orderB) return orderA - orderB;
      return (a.menuCode || a.MenuCode || "").localeCompare(b.menuCode || b.MenuCode || "");
    });

    submenus.sort((a, b) => {
      const orderA = a.displayOrder || a.DisplayOrder || 0;
      const orderB = b.displayOrder || b.DisplayOrder || 0;
      if (orderA !== orderB) return orderA - orderB;
      return (a.menuCode || a.MenuCode || "").localeCompare(b.menuCode || b.MenuCode || "");
    });

    // Sort submenus within each parent
    submenusByParent.forEach((subs) => {
      subs.sort((a, b) => {
        const orderA = a.displayOrder || a.DisplayOrder || 0;
        const orderB = b.displayOrder || b.DisplayOrder || 0;
        if (orderA !== orderB) return orderA - orderB;
        return (a.menuCode || a.MenuCode || "").localeCompare(b.menuCode || b.MenuCode || "");
      });
    });

    return { rootMenus, submenus, submenusByParent };
  };

  const mapPrivilegesListToState = (privilegesList) => {
    const toBool = (v) => v === true || v === 1 || v === "1" || v === "true";
    const privilegesMap = {};
    (privilegesList || []).forEach((priv) => {
      const menuCode = priv.menuCode || priv.MenuCode;
      if (!menuCode) return;
      privilegesMap[menuCode] = {
        viewPermission: toBool(priv.viewPermission ?? priv.ViewPermission),
        addPermission: toBool(priv.addPermission ?? priv.AddPermission),
        editPermission: toBool(priv.editPermission ?? priv.EditPermission),
        deletePermission: toBool(priv.deletePermission ?? priv.DeletePermission),
        downloadPermission: toBool(priv.downloadPermission ?? priv.DownloadPermission),
        isActive:
          priv.isActive !== undefined
            ? toBool(priv.isActive)
            : priv.IsActive !== undefined
              ? toBool(priv.IsActive)
              : true,
      };
    });
    return privilegesMap;
  };

  const loadPrivilegesForTarget = async ({ userTypeCode, userIdCode }) => {
    const response = await privilegeAPI.getAll({
      viewMode: userIdCode ? "user" : "userType",
      userTypeCode: userIdCode ? undefined : userTypeCode || undefined,
      userIdCode: userIdCode || undefined,
      isActive: true,
      pageNumber: 1,
      pageSize: 5000,
    });

    if (response.success && response.data) {
      const privilegesList =
        response.data.privileges || response.data.Privileges || response.data.data || [];
      setPrivileges(mapPrivilegesListToState(privilegesList));
    } else {
      setPrivileges({});
    }
  };

  /** Role privileges + user-wise overrides (same effective set as User Wise grid). */
  const loadEffectivePrivilegesForUser = async (userId, roleCode) => {
    if (!userId) {
      setPrivileges({});
      return;
    }

    let merged = {};

    if (roleCode) {
      const roleResponse = await privilegeAPI.getAll({
        viewMode: "userType",
        userTypeCode: roleCode,
        isActive: true,
        pageNumber: 1,
        pageSize: 5000,
      });
      if (roleResponse.success && roleResponse.data) {
        const roleList =
          roleResponse.data.privileges ||
          roleResponse.data.Privileges ||
          roleResponse.data.data ||
          [];
        merged = mapPrivilegesListToState(roleList);
      }
    }

    const userResponse = await privilegeAPI.getAll({
      viewMode: "user",
      userIdCode: userId,
      isActive: true,
      pageNumber: 1,
      pageSize: 5000,
    });
    if (userResponse.success && userResponse.data) {
      const userList =
        userResponse.data.privileges ||
        userResponse.data.Privileges ||
        userResponse.data.data ||
        [];
      merged = { ...merged, ...mapPrivilegesListToState(userList) };
    }

    if (isMountedRef.current) {
      setPrivileges(merged);
    }
  };

  const loadPrivilegeForEdit = async (code) => {
    if (!code || editLoadedRef.current) return;
    try {
      setLoading(true);
      const response = await privilegeAPI.getById(code);
      if (!response?.success || !response.data) {
        await Swal.fire("Error", response?.message || "Privilege not found", "error");
        goBackToList();
        return;
      }

      const priv = response.data;
      const userId = (priv.userIdCode || priv.UserIdCode || "").trim();
      const userType = (priv.userTypeCode || priv.UserTypeCode || "").trim();

      if (userId) {
        setAssignmentMode("user");
        const usersResponse = await userAPI.getAll({ status: "active" });
        const usersList =
          usersResponse?.success && usersResponse.data ? usersResponse.data || [] : [];
        setDropdowns((prev) => ({ ...prev, users: usersList }));

        const matchedUser = usersList.find(
          (u) => (u.userIdCode || u.UserIdCode) === userId
        );
        const roleCode =
          userType ||
          matchedUser?.userTypeCode ||
          matchedUser?.UserTypeCode ||
          "";

        setSelectedUserType("");
        setSelectedUserTypeForUser(roleCode);
        setSelectedUser(userId);
        // Load role base + user overrides (empty user rows alone show blank matrix)
        await loadEffectivePrivilegesForUser(userId, roleCode);
      } else if (userType) {
        setAssignmentMode("userType");
        setSelectedUser("");
        setSelectedUserTypeForUser("");
        setSelectedUserType(userType);
        await loadPrivilegesForTarget({ userTypeCode: userType });
        try {
          const countResponse = await privilegeAPI.getActiveUserCountByRole(userType);
          if (countResponse?.success && countResponse.data) {
            setRoleUserCount(
              countResponse.data.userCount ?? countResponse.data.UserCount ?? 0
            );
          }
        } catch {
          setRoleUserCount(null);
        }
      } else {
        await Swal.fire(
          "Error",
          "This privilege has no User Role or User assigned.",
          "error"
        );
        goBackToList();
        return;
      }

      editLoadedRef.current = true;
    } catch (error) {
      console.error("Error loading privilege for edit:", error);
      await Swal.fire("Error", "Failed to load privilege for edit", "error");
      goBackToList();
    } finally {
      setLoading(false);
    }
  };

  const loadUserMappingForEdit = async (userId, roleCodeHint = "") => {
    if (!userId) return;
    try {
      setLoading(true);
      setAssignmentMode("user");

      const usersResponse = await userAPI.getAll({ status: "active" });
      const usersList =
        usersResponse?.success && usersResponse.data ? usersResponse.data || [] : [];
      setDropdowns((prev) => ({ ...prev, users: usersList }));

      const matchedUser = usersList.find(
        (u) => (u.userIdCode || u.UserIdCode) === userId
      );
      const roleCode =
        roleCodeHint ||
        matchedUser?.userTypeCode ||
        matchedUser?.UserTypeCode ||
        "";

      setSelectedUserType("");
      setSelectedUserTypeForUser(roleCode);
      setSelectedUser(userId);

      await loadEffectivePrivilegesForUser(userId, roleCode);
      editLoadedRef.current = true;
    } catch (error) {
      console.error("Error loading user privileges for edit:", error);
      await Swal.fire("Error", "Failed to load user privileges for edit", "error");
      goBackToList();
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  const loadDropdowns = async () => {
    try {
      const userTypesResponse = await masterDataAPI.getUserTypes();

      let menusResponse;
      if (useAdminMenuLabels) {
        try {
          menusResponse = await menuAPI.getAllMenusForPrivilegePages();
        } catch (e) {
          console.warn("Privilege pages menu API failed (old API build or network):", e);
          menusResponse = { success: false };
        }
        if (!menusResponse?.success || menusResponse.data == null) {
          menusResponse = await menuAPI.getAllMenus();
        }
      } else {
        menusResponse = await menuAPI.getAllMenus();
      }

      if (userTypesResponse.success && userTypesResponse.data) {
        setDropdowns((prev) => ({
          ...prev,
          userTypes: userTypesResponse.data,
        }));
      }

      if (menusResponse.success && menusResponse.data) {
        const organized = organizeMenus(menusResponse.data);
        setDropdowns((prev) => ({
          ...prev,
          organizedMenus: organized,
        }));
      } else {
        throw new Error(menusResponse?.message || "Menus could not be loaded");
      }

      if (isEditMode && privCode) {
        await loadPrivilegeForEdit(privCode);
      } else if (isUserMappingEdit && preloadUserIdCode) {
        await loadUserMappingForEdit(preloadUserIdCode, preloadUserTypeCode);
      }
    } catch (error) {
      console.error("Error loading dropdowns:", error);
      const detail =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to load dropdown data";
      Swal.fire("Error", detail, "error");
    }
  };

  const loadUsers = async () => {
    try {
      const response = await userAPI.getAll({ status: "active" });
      if (response.success && response.data) {
        setDropdowns((prev) => ({
          ...prev,
          users: response.data || [],
        }));
      }
    } catch (error) {
      console.error("Error loading users:", error);
    }
  };

  useEffect(() => {
    if (assignmentMode === "user") {
      loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentMode]);

  const handleUserTypeChange = async (userTypeCode) => {
    setSelectedUserType(userTypeCode);
    setRoleUserCount(null);
    if (!userTypeCode) {
      setPrivileges({});
      return;
    }

    try {
      setLoading(true);
      const [privResult, countResponse] = await Promise.all([
        loadPrivilegesForTarget({ userTypeCode }),
        privilegeAPI.getActiveUserCountByRole(userTypeCode).catch(() => null),
      ]);
      void privResult;
      if (countResponse?.success && countResponse.data) {
        const count =
          countResponse.data.userCount ??
          countResponse.data.UserCount ??
          0;
        setRoleUserCount(count);
      } else {
        // Fallback: count from already-loaded users dropdown
        const fallback = dropdowns.users.filter(
          (u) =>
            (u.userTypeCode || u.UserTypeCode) === userTypeCode &&
            (u.isActive === true || u.isActive === 1 || u.IsActive === true || u.IsActive === 1 || u.isActive == null)
        ).length;
        setRoleUserCount(fallback);
      }
    } catch (error) {
      console.error("Error loading privileges:", error);
      Swal.fire("Error", "Failed to load privileges", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUserChange = async (userIdCode) => {
    setSelectedUser(userIdCode);
    if (!userIdCode) {
      setPrivileges({});
      return;
    }

    try {
      setLoading(true);
      await loadPrivilegesForTarget({ userIdCode });
    } catch (error) {
      console.error("Error loading privileges:", error);
      Swal.fire("Error", "Failed to load privileges", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (menuCode, permission, value) => {
    setPrivileges((prev) => {
      const currentPrivilege = prev[menuCode] || {
        viewPermission: false,
        addPermission: false,
        editPermission: false,
        deletePermission: false,
        downloadPermission: false,
        isActive: true,
      };
      
      return {
        ...prev,
        [menuCode]: {
          ...currentPrivilege,
          [permission]: value,
          // If isActive is being changed, use the new value; otherwise preserve existing value
          isActive: permission === "isActive" ? value : (currentPrivilege.isActive !== false),
        },
      };
    });
  };

  // Check if all permissions are checked for a menu/submenu
  const areAllPermissionsChecked = (menuCode) => {
    const menuPrivileges = privileges[menuCode] || {
      viewPermission: false,
      addPermission: false,
      editPermission: false,
      deletePermission: false,
      downloadPermission: false,
      isActive: true,
    };
    
    return (
      menuPrivileges.viewPermission &&
      menuPrivileges.addPermission &&
      menuPrivileges.editPermission &&
      menuPrivileges.deletePermission &&
      menuPrivileges.downloadPermission &&
      menuPrivileges.isActive !== false
    );
  };

  // Handle check all for a menu/submenu
  const handleCheckAll = (menuCode, checked) => {
    setPrivileges((prev) => {
      const currentPrivilege = prev[menuCode] || {
        viewPermission: false,
        addPermission: false,
        editPermission: false,
        deletePermission: false,
        downloadPermission: false,
        isActive: true,
      };
      
      return {
        ...prev,
        [menuCode]: {
          viewPermission: checked,
          addPermission: checked,
          editPermission: checked,
          deletePermission: checked,
          downloadPermission: checked,
          isActive: checked,
        },
      };
    });
  };

  const toggleMenu = (menuCode) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuCode]: !prev[menuCode],
    }));
  };

  const handleSave = async () => {
    if (!canSave) {
      Swal.fire("Access Denied", "You do not have permission to save privileges.", "error");
      return;
    }
    let userTypeCodeToUse = null;
    let userIdCodeToUse = null;
    
    if (assignmentMode === "userType") {
      if (!selectedUserType || selectedUserType.trim() === "") {
        Swal.fire("Error", "Please select a User Role", "error");
        return;
      }
      userTypeCodeToUse = selectedUserType.trim();
    } else {
      if (!selectedUser || selectedUser.trim() === "" || !selectedUserTypeForUser || selectedUserTypeForUser.trim() === "") {
        Swal.fire("Error", "Please select a User Role and a User", "error");
        return;
      }
      userIdCodeToUse = selectedUser.trim();
    }

    const createdBy = currentUser?.userIdCode || currentUser?.UserIdCode || "SYSADMIN001";
    const privilegeList = [];

    Object.keys(privileges).forEach((menuCode) => {
      const priv = privileges[menuCode];
      privilegeList.push({
        userTypeCode: userTypeCodeToUse || "",
        userIdCode: userIdCodeToUse || null,
        menuCode: menuCode,
        viewPermission: priv.viewPermission || false,
        addPermission: priv.addPermission || false,
        editPermission: priv.editPermission || false,
        deletePermission: priv.deletePermission || false,
        downloadPermission: priv.downloadPermission || false,
        isActive: priv.isActive !== false,
        createdBy: createdBy,
      });
    });

    if (privilegeList.length === 0) {
      Swal.fire("Warning", "No privileges to save", "warning");
      return;
    }

    setSaving(true);
    try {
      // Build request object - only include the relevant field (not null)
      const requestData = {
        privileges: privilegeList,
        modifiedBy: createdBy,
      };
      
      // Only include userTypeCode or userIdCode, not both
      if (assignmentMode === "userType" && userTypeCodeToUse) {
        requestData.userTypeCode = userTypeCodeToUse;
      } else if (assignmentMode === "user" && userIdCodeToUse) {
        requestData.userIdCode = userIdCodeToUse;
      }
      
      const response = await privilegeAPI.bulkUpdate(requestData);

      if (response && response.success) {
        Swal.fire({
          title: "Success",
          text: response.message || "Privileges saved successfully",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          goBackToList();
        });
      } else {
        Swal.fire("Error", response.message || "Failed to save privileges", "error");
      }
    } catch (error) {
      console.error("Error saving privileges:", error);
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to save privileges. Please try again.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const renderMenuSection = (menu) => {
    const menuCode = menu.menuCode || menu.MenuCode;
    const menuName =
      menu.adminMenuName ||
      menu.AdminMenuName ||
      menu.menuName ||
      menu.MenuName ||
      "";
    const pageName = menu.pageName || menu.PageName || "";
    const submenus = dropdowns.organizedMenus?.submenusByParent?.get(menuCode) || [];
    const isExpanded = expandedMenus[menuCode] || false;
    const hasSubmenus = submenus.length > 0;
    
    // Check if any submenu has a pageName
    const hasSubmenusWithPageName = submenus.some(
      (sub) => (sub.pageName || sub.PageName || "") !== ""
    );

    // Get privileges for this menu
    const menuPrivileges = privileges[menuCode] || {
      viewPermission: false,
      addPermission: false,
      editPermission: false,
      deletePermission: false,
      downloadPermission: false,
      isActive: true,
    };

    return (
      <div key={menuCode} className="mb-3">
        <div className="card shadow-sm">
          <div
            className="card-header d-flex justify-content-between align-items-center"
            style={{
              backgroundColor: "#f5f5f5",
              cursor: hasSubmenus ? "pointer" : "default",
              padding: "12px 16px",
            }}
            onClick={() => hasSubmenus && toggleMenu(menuCode)}
          >
            <h6 className="mb-0 fw-bold flex-grow-1">{menuName}</h6>
            {hasSubmenus && (
              <i
                className={`bi ${isExpanded ? "bi-chevron-up" : "bi-chevron-down"}`}
                style={{ fontSize: "1.2rem", color: "#666" }}
              />
            )}
          </div>

          {/* If menu has submenus, use collapse; if no submenus but has pageName, always show */}
          {hasSubmenus ? (
            <div className={`collapse ${isExpanded ? "show" : ""}`}>
              <div className="card-body">
                {/* Permissions for this menu - Show if menu has pageName */}
                {pageName && (
                <div className="permission-section mb-3 p-3 bg-light rounded border border-2">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: "14px" }}>
                      {menuName} - Permissions
                    </h6>
                    <div className="form-check form-switch d-flex align-items-center">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={areAllPermissionsChecked(menuCode)}
                        onChange={(e) => handleCheckAll(menuCode, e.target.checked)}
                        id={`${menuCode}-checkAll`}
                        style={{
                          width: "3rem",
                          height: "1.5rem",
                          cursor: "pointer"
                        }}
                      />
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-4">
                    <div className="form-check permission-checkbox">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={menuPrivileges.viewPermission}
                        onChange={(e) =>
                          handlePermissionChange(menuCode, "viewPermission", e.target.checked)
                        }
                        id={`${menuCode}-view`}
                        style={{
                          transform: "scale(1.3)",
                          width: "20px",
                          height: "20px",
                          cursor: "pointer"
                        }}
                      />
                      <label className="form-check-label fw-semibold ms-2" htmlFor={`${menuCode}-view`} style={{ fontSize: "15px", cursor: "pointer" }}>
                        View
                      </label>
                    </div>
                    <div className="form-check permission-checkbox">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={menuPrivileges.addPermission}
                        onChange={(e) =>
                          handlePermissionChange(menuCode, "addPermission", e.target.checked)
                        }
                        id={`${menuCode}-add`}
                        style={{
                          transform: "scale(1.3)",
                          width: "20px",
                          height: "20px",
                          cursor: "pointer"
                        }}
                      />
                      <label className="form-check-label fw-semibold ms-2" htmlFor={`${menuCode}-add`} style={{ fontSize: "15px", cursor: "pointer" }}>
                        Add
                      </label>
                    </div>
                    <div className="form-check permission-checkbox">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={menuPrivileges.editPermission}
                        onChange={(e) =>
                          handlePermissionChange(menuCode, "editPermission", e.target.checked)
                        }
                        id={`${menuCode}-edit`}
                        style={{
                          transform: "scale(1.3)",
                          width: "20px",
                          height: "20px",
                          cursor: "pointer"
                        }}
                      />
                      <label className="form-check-label fw-semibold ms-2" htmlFor={`${menuCode}-edit`} style={{ fontSize: "15px", cursor: "pointer" }}>
                        Edit
                      </label>
                    </div>
                    <div className="form-check permission-checkbox">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={menuPrivileges.deletePermission}
                        onChange={(e) =>
                          handlePermissionChange(menuCode, "deletePermission", e.target.checked)
                        }
                        id={`${menuCode}-delete`}
                        style={{
                          transform: "scale(1.3)",
                          width: "20px",
                          height: "20px",
                          cursor: "pointer"
                        }}
                      />
                      <label className="form-check-label fw-semibold ms-2" htmlFor={`${menuCode}-delete`} style={{ fontSize: "15px", cursor: "pointer" }}>
                        Delete
                      </label>
                    </div>
                    <div className="form-check permission-checkbox">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={menuPrivileges.downloadPermission}
                        onChange={(e) =>
                          handlePermissionChange(menuCode, "downloadPermission", e.target.checked)
                        }
                        id={`${menuCode}-download`}
                        style={{
                          transform: "scale(1.3)",
                          width: "20px",
                          height: "20px",
                          cursor: "pointer"
                        }}
                      />
                      <label className="form-check-label fw-semibold ms-2" htmlFor={`${menuCode}-download`} style={{ fontSize: "15px", cursor: "pointer" }}>
                        Download
                      </label>
                    </div>
                    <div className="form-check permission-checkbox">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={menuPrivileges.isActive !== false}
                        onChange={(e) =>
                          handlePermissionChange(menuCode, "isActive", e.target.checked)
                        }
                        id={`${menuCode}-isActive`}
                        style={{
                          transform: "scale(1.3)",
                          width: "20px",
                          height: "20px",
                          cursor: "pointer"
                        }}
                      />
                      <label className="form-check-label fw-semibold ms-2" htmlFor={`${menuCode}-isActive`} style={{ fontSize: "15px", cursor: "pointer" }}>
                        Is Active
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Submenus */}
              {hasSubmenus && (
                <div className="mt-3 ps-3 border-start border-2">
                  {submenus.map((submenu) => {
                    const submenuCode = submenu.menuCode || submenu.MenuCode;
                    const submenuName =
                      submenu.adminMenuName ||
                      submenu.AdminMenuName ||
                      submenu.menuName ||
                      submenu.MenuName ||
                      "";
                    const submenuPageName = submenu.pageName || submenu.PageName || "";
                    const submenuPrivileges = privileges[submenuCode] || {
                      viewPermission: false,
                      addPermission: false,
                      editPermission: false,
                      deletePermission: false,
                      downloadPermission: false,
                      isActive: true,
                    };

                    // Only show permissions if submenu has a pageName
                    if (!submenuPageName) {
                      return null;
                    }

                    return (
                      <div 
                        key={submenuCode} 
                        className="mb-3 p-3 bg-light rounded border border-2"
                      >
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: "15px" }}>
                            {submenuName}
                          </h6>
                          <div className="form-check form-switch d-flex align-items-center">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              checked={areAllPermissionsChecked(submenuCode)}
                              onChange={(e) => handleCheckAll(submenuCode, e.target.checked)}
                              id={`${submenuCode}-checkAll`}
                              style={{
                                width: "3rem",
                                height: "1.5rem",
                                cursor: "pointer"
                              }}
                            />
                          </div>
                        </div>
                        <div className="d-flex flex-wrap gap-4">
                          <div className="form-check permission-checkbox">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={submenuPrivileges.viewPermission}
                              onChange={(e) =>
                                handlePermissionChange(submenuCode, "viewPermission", e.target.checked)
                              }
                              id={`${submenuCode}-view`}
                              style={{
                                transform: "scale(1.3)",
                                width: "20px",
                                height: "20px",
                                cursor: "pointer"
                              }}
                            />
                            <label className="form-check-label fw-semibold ms-2" htmlFor={`${submenuCode}-view`} style={{ fontSize: "15px", cursor: "pointer" }}>
                              View
                            </label>
                          </div>
                          <div className="form-check permission-checkbox">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={submenuPrivileges.addPermission}
                              onChange={(e) =>
                                handlePermissionChange(submenuCode, "addPermission", e.target.checked)
                              }
                              id={`${submenuCode}-add`}
                              style={{
                                transform: "scale(1.3)",
                                width: "20px",
                                height: "20px",
                                cursor: "pointer"
                              }}
                            />
                            <label className="form-check-label fw-semibold ms-2" htmlFor={`${submenuCode}-add`} style={{ fontSize: "15px", cursor: "pointer" }}>
                              Add
                            </label>
                          </div>
                          <div className="form-check permission-checkbox">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={submenuPrivileges.editPermission}
                              onChange={(e) =>
                                handlePermissionChange(submenuCode, "editPermission", e.target.checked)
                              }
                              id={`${submenuCode}-edit`}
                              style={{
                                transform: "scale(1.3)",
                                width: "20px",
                                height: "20px",
                                cursor: "pointer"
                              }}
                            />
                            <label className="form-check-label fw-semibold ms-2" htmlFor={`${submenuCode}-edit`} style={{ fontSize: "15px", cursor: "pointer" }}>
                              Edit
                            </label>
                          </div>
                          <div className="form-check permission-checkbox">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={submenuPrivileges.deletePermission}
                              onChange={(e) =>
                                handlePermissionChange(submenuCode, "deletePermission", e.target.checked)
                              }
                              id={`${submenuCode}-delete`}
                              style={{
                                transform: "scale(1.3)",
                                width: "20px",
                                height: "20px",
                                cursor: "pointer"
                              }}
                            />
                            <label className="form-check-label fw-semibold ms-2" htmlFor={`${submenuCode}-delete`} style={{ fontSize: "15px", cursor: "pointer" }}>
                              Delete
                            </label>
                          </div>
                          <div className="form-check permission-checkbox">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={submenuPrivileges.downloadPermission}
                              onChange={(e) =>
                                handlePermissionChange(submenuCode, "downloadPermission", e.target.checked)
                              }
                              id={`${submenuCode}-download`}
                              style={{
                                transform: "scale(1.3)",
                                width: "20px",
                                height: "20px",
                                cursor: "pointer"
                              }}
                            />
                            <label className="form-check-label fw-semibold ms-2" htmlFor={`${submenuCode}-download`} style={{ fontSize: "15px", cursor: "pointer" }}>
                              Download
                            </label>
                          </div>
                          <div className="form-check permission-checkbox">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={submenuPrivileges.isActive !== false}
                              onChange={(e) =>
                                handlePermissionChange(submenuCode, "isActive", e.target.checked)
                              }
                              id={`${submenuCode}-isActive`}
                              style={{
                                transform: "scale(1.3)",
                                width: "20px",
                                height: "20px",
                                cursor: "pointer"
                              }}
                            />
                            <label className="form-check-label fw-semibold ms-2" htmlFor={`${submenuCode}-isActive`} style={{ fontSize: "15px", cursor: "pointer" }}>
                              Is Active
                            </label>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          ) : (
            /* Menu without submenus but with pageName - show permissions directly */
            pageName && (
              <div className="card-body">
                <div className="permission-section mb-3 p-3 bg-light rounded border border-2">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: "14px" }}>
                      {menuName} - Permissions
                    </h6>
                    <div className="form-check form-switch d-flex align-items-center">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={areAllPermissionsChecked(menuCode)}
                        onChange={(e) => handleCheckAll(menuCode, e.target.checked)}
                        id={`${menuCode}-checkAll`}
                        style={{
                          width: "3rem",
                          height: "1.5rem",
                          cursor: "pointer"
                        }}
                      />
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-4">
                    <div className="form-check permission-checkbox">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={menuPrivileges.viewPermission}
                        onChange={(e) =>
                          handlePermissionChange(menuCode, "viewPermission", e.target.checked)
                        }
                        id={`${menuCode}-view`}
                        style={{
                          transform: "scale(1.3)",
                          width: "20px",
                          height: "20px",
                          cursor: "pointer"
                        }}
                      />
                      <label className="form-check-label fw-semibold ms-2" htmlFor={`${menuCode}-view`} style={{ fontSize: "15px", cursor: "pointer" }}>
                        View
                      </label>
                    </div>
                    <div className="form-check permission-checkbox">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={menuPrivileges.addPermission}
                        onChange={(e) =>
                          handlePermissionChange(menuCode, "addPermission", e.target.checked)
                        }
                        id={`${menuCode}-add`}
                        style={{
                          transform: "scale(1.3)",
                          width: "20px",
                          height: "20px",
                          cursor: "pointer"
                        }}
                      />
                      <label className="form-check-label fw-semibold ms-2" htmlFor={`${menuCode}-add`} style={{ fontSize: "15px", cursor: "pointer" }}>
                        Add
                      </label>
                    </div>
                    <div className="form-check permission-checkbox">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={menuPrivileges.editPermission}
                        onChange={(e) =>
                          handlePermissionChange(menuCode, "editPermission", e.target.checked)
                        }
                        id={`${menuCode}-edit`}
                        style={{
                          transform: "scale(1.3)",
                          width: "20px",
                          height: "20px",
                          cursor: "pointer"
                        }}
                      />
                      <label className="form-check-label fw-semibold ms-2" htmlFor={`${menuCode}-edit`} style={{ fontSize: "15px", cursor: "pointer" }}>
                        Edit
                      </label>
                    </div>
                    <div className="form-check permission-checkbox">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={menuPrivileges.deletePermission}
                        onChange={(e) =>
                          handlePermissionChange(menuCode, "deletePermission", e.target.checked)
                        }
                        id={`${menuCode}-delete`}
                        style={{
                          transform: "scale(1.3)",
                          width: "20px",
                          height: "20px",
                          cursor: "pointer"
                        }}
                      />
                      <label className="form-check-label fw-semibold ms-2" htmlFor={`${menuCode}-delete`} style={{ fontSize: "15px", cursor: "pointer" }}>
                        Delete
                      </label>
                    </div>
                    <div className="form-check permission-checkbox">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={menuPrivileges.downloadPermission}
                        onChange={(e) =>
                          handlePermissionChange(menuCode, "downloadPermission", e.target.checked)
                        }
                        id={`${menuCode}-download`}
                        style={{
                          transform: "scale(1.3)",
                          width: "20px",
                          height: "20px",
                          cursor: "pointer"
                        }}
                      />
                      <label className="form-check-label fw-semibold ms-2" htmlFor={`${menuCode}-download`} style={{ fontSize: "15px", cursor: "pointer" }}>
                        Download
                      </label>
                    </div>
                    <div className="form-check permission-checkbox">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={menuPrivileges.isActive !== false}
                        onChange={(e) =>
                          handlePermissionChange(menuCode, "isActive", e.target.checked)
                        }
                        id={`${menuCode}-isActive`}
                        style={{
                          transform: "scale(1.3)",
                          width: "20px",
                          height: "20px",
                          cursor: "pointer"
                        }}
                      />
                      <label className="form-check-label fw-semibold ms-2" htmlFor={`${menuCode}-isActive`} style={{ fontSize: "15px", cursor: "pointer" }}>
                        Is Active
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="container-fluid" style={{ padding: "24px" }}>
      <div className="add-edit-user-header">
        <nav aria-label="breadcrumb" className="mb-0">
          <ol className="breadcrumb add-edit-user-breadcrumb mb-0">
            <li className="breadcrumb-item">
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none breadcrumb-link"
                onClick={goBackToList}
                style={{ fontSize: "14px" }}
              >
                Privilege Management
              </button>
            </li>
            <li className="breadcrumb-item active breadcrumb-current brdcrmbLstitms brdcrmbCurPg" aria-current="page">
              {isLockedTarget ? "Edit Privileges" : "Assign Privileges"}
            </li>
          </ol>
        </nav>
        <div className="add-edit-user-header-right">
          <Button
            className="add-edit-user-back-button"
            onClick={goBackToList}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        </div>
      </div>

      <div className="card-panel">
        <h5 className="mb-4 fw-bold">{isLockedTarget ? "Edit Privileges" : "Assign Privileges"}</h5>

        <div className="d-flex flex-column gap-4">
          {/* Assignment Mode Selection */}
          <div>
            <label className="form-label fw-bold mb-2">Assignment Mode *</label>
            <div className="d-flex gap-4">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="assignmentMode"
                  id="mode-userType"
                  value="userType"
                  checked={assignmentMode === "userType"}
                  disabled={isLockedTarget}
                  onChange={(e) => {
                    setAssignmentMode(e.target.value);
                    setSelectedUserType("");
                    setSelectedUserTypeForUser("");
                    setSelectedUser("");
                    setRoleUserCount(null);
                    setPrivileges({});
                  }}
                />
                <label className="form-check-label" htmlFor="mode-userType">
                  User Role Wise
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="assignmentMode"
                  id="mode-user"
                  value="user"
                  checked={assignmentMode === "user"}
                  disabled={isLockedTarget}
                  onChange={(e) => {
                    setAssignmentMode(e.target.value);
                    setSelectedUserType("");
                    setSelectedUserTypeForUser("");
                    setSelectedUser("");
                    setRoleUserCount(null);
                    setPrivileges({});
                  }}
                />
                <label className="form-check-label" htmlFor="mode-user">
                  User Wise
                </label>
              </div>
            </div>
          </div>

          {/* User Role Selection */}
          {assignmentMode === "userType" && (
            <div>
              <label className="form-label">Select User Role *</label>
              <select
                ref={userTypeSelectRef}
                className="form-select form-control-light"
                value={selectedUserType}
                onChange={(e) => handleUserTypeChange(e.target.value)}
                style={{ height: "42px" }}
              >
                <option value="">Select User Role</option>
                {dropdowns.userTypes.map((type) => (
                  <option key={type.userTypeCode} value={type.userTypeCode}>
                    {type.userTypeName}
                  </option>
                ))}
              </select>
              {selectedUserType && roleUserCount !== null && (
                <div className="form-text mt-1" style={{ fontSize: "13px", color: "#555" }}>
                  Active users under this role: <strong>{roleUserCount}</strong>
                </div>
              )}
            </div>
          )}

          {/* User Role and User Selection (in user mode) */}
          {assignmentMode === "user" && (
            <>
              <div>
                <label className="form-label">Select User Role *</label>
                <select
                  ref={userTypeForUserSelectRef}
                  className="form-select form-control-light"
                  value={selectedUserTypeForUser}
                  disabled={isUserMappingEdit}
                  onChange={(e) => {
                    setSelectedUserTypeForUser(e.target.value);
                    setSelectedUser("");
                    setPrivileges({});
                  }}
                  style={{ height: "42px" }}
                >
                  <option value="">Select User Role</option>
                  {dropdowns.userTypes.map((type) => (
                    <option key={type.userTypeCode} value={type.userTypeCode}>
                      {type.userTypeName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Select User *</label>
                <select
                  ref={userSelectRef}
                  className="form-select form-control-light"
                  value={selectedUser}
                  onChange={(e) => handleUserChange(e.target.value)}
                  disabled={isUserMappingEdit || !selectedUserTypeForUser}
                  style={{ height: "42px" }}
                >
                  <option value="">
                    {selectedUserTypeForUser ? "Select User" : "Select User Role First"}
                  </option>
                  {filteredUsers.map((user) => (
                    <option key={user.userIdCode} value={user.userIdCode}>
                      {user.fullName || user.userName || user.userIdCode}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* Menu matrix — keep mounted while loading to avoid page jump */}
          {((assignmentMode === "userType" && selectedUserType) ||
            (assignmentMode === "user" && selectedUser && selectedUserTypeForUser)) && (
            <div className="privilege-matrix-wrap">
              {loading && (
                <div className="privilege-matrix-loading" aria-live="polite">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}
              <div className={loading ? "privilege-matrix-content is-loading" : "privilege-matrix-content"}>
              {dropdowns.organizedMenus?.rootMenus?.map((menu) => {
                const pageName = menu.pageName || menu.PageName || "";
                const submenus = dropdowns.organizedMenus?.submenusByParent?.get(menu.menuCode || menu.MenuCode) || [];
                
                // If menu has submenus, use accordion style (shows parent + submenu permissions)
                if (submenus.length > 0) {
                  return renderMenuSection(menu);
                }
                // If menu is standalone with pageName, show as separate card
                if (pageName) {
                  return renderMenuSection(menu);
                }
                return null;
              })}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="row mt-4">
            <div className="col-12">
              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  onClick={goBackToList}
                  className="btn btn-cancel-user funbtns"
                >
                  <CancelIcon style={{ fontSize: "18px" }} />
                  <span style={{ marginLeft: "4px" }}>Cancel</span>
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={
                    !canSave ||
                    saving ||
                    (assignmentMode === "userType" && !selectedUserType) ||
                    (assignmentMode === "user" && (!selectedUser || !selectedUserTypeForUser))
                  }
                  className="btn btn-save-user funbtns"
                >
                  {saving ? (
                    <>
                      <div className="spinner-border spinner-border-sm" role="status" style={{ marginRight: "4px" }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <SaveIcon style={{ fontSize: "18px" }} />
                      <span style={{ marginLeft: "4px" }}>Save</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddEditPrivilege;
